
<template>
  <div class="attachment-wrapper">
    <!-- Upload Button -->
    <div v-if="!readonly" class="attachment-upload">
      <button 
        type="button" 
        class="btn btn-sm btn-outline-secondary" 
        @click="$refs.fileInput.click()"
        :disabled="uploading"
      >
        <i class="fas fa-paperclip"></i> 
        {{ uploading ? 'Uploading...' : 'Attach File' }}
      </button>
      <input 
        type="file" 
        ref="fileInput" 
        style="display:none" 
        @change="handleFileSelect($event)"
        :accept="accept"
        multiple
      />
    </div>
    
    <!-- File List -->
    <div v-if="files && files.length > 0" class="attachment-grid" ref="attachmentGrid">
      <div 
        v-for="(file, idx) in files" 
        :key="file.id || idx" 
        class="attachment-card"
        @click="openAttachmentModal(file, idx)"
      >
        <!-- Image Preview -->
        <div v-if="isImage(file)" class="attachment-image-wrapper">
          <img :src="getPath(file)" class="attachment-image" />
        </div>
        <!-- File Icon for non-images -->
        <div v-else class="attachment-file-icon">
          <i class="fas fa-file fa-3x text-muted"></i>
        </div>
        
        <!-- File Name -->
        <div v-if="!isImage(file)" class="attachment-name">{{ file.file_name }}</div>
        
        <!-- Note Display -->
        <div v-if="file.note" class="attachment-note-display">
          <i class="fas fa-comment"></i>
          <span>{{ file.note }}</span>
        </div>
        
        <!-- Remove Button -->
        <button 
          v-if="!readonly" 
          class="btn btn-sm btn-danger attachment-remove" 
          @click.stop="confirmRemove(idx)"
          title="Remove file"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- FormModal Component -->
    <FormModal ref="attachmentModal" title="Attachment Details">
      <template #default>
        <div v-if="selectedFile">
          <!-- File Preview -->
          <div class="modal-file-preview">
            <div v-if="isImage(selectedFile)" class="modal-image-wrapper">
              <img :src="getPath(selectedFile)" class="modal-image" />
            </div>
            <div v-else class="modal-file-icon">
              <i class="fas fa-file fa-4x text-muted"></i>
              <span class="modal-file-name">{{ selectedFile?.file_name }}</span>
            </div>
          </div>

          <!-- Note Input -->
          <div class="modal-note-wrapper">
            <label class="form-label form-label-thin">Note</label>
            <textarea 
              class="form-control form-control-thin" 
              v-model="selectedFileNote" 
              placeholder="Add a note for this attachment..."
              rows="3"
            ></textarea>
          </div>

          <!-- Buttons -->
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveNote">Save Note</button>
          </div>
        </div>
      </template>
    </FormModal>
  </div>
</template>

<script>
import FormModal from '@/components/utils/FormModal.vue';

export default {
  name: 'AttachmentList',
  components: {
    FormModal
  },
  props: {
    files: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    uploading: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: '.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx'
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFileIndex: -1,
      selectedFileNote: '',
      lastFilesLength: 0
    }
  },
  mounted() {
    this.lastFilesLength = Array.isArray(this.files) ? this.files.length : 0
  },
  updated() {
    const currentCount = Array.isArray(this.files) ? this.files.length : 0

    if (!this.readonly && currentCount > this.lastFilesLength) {
      this.lastFilesLength = currentCount

      this.$nextTick(() => {
        const newFile = this.files[this.files.length - 1]
        const newIndex = this.files.length - 1

        if (newFile) {
          this.openAttachmentModal(newFile, newIndex)
        }
      })
    }
  },
  methods: {
    getPath(file) {
      return this.$backend_domain + file.file_path
    },
    
    openInNewTab(url) {
      window.open(url, '_blank')
    },
    
    handleFileSelect(event) {
      const files = event.target.files
      if (files.length > 0) {
        this.$emit('select', files)
      }
      event.target.value = ''
    },
    
    isImage(file) {
      const name = file.file_name || ''
      const extension = name.split('.').pop().toLowerCase()
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']
      return imageExtensions.includes(extension)
    },
    
    confirmRemove(idx) {
      if (confirm('Are you sure you want to remove this file?')) {
        this.$emit('remove', idx)
      }
    },
    
    openAttachmentModal(file, idx) {
      if (this.readonly) {
        this.openInNewTab(this.getPath(file))
        return
      }
      this.selectedFile = file
      this.selectedFileIndex = idx
      this.selectedFileNote = file.note || ''
      this.$refs.attachmentModal.showModal()
    },
    
    closeModal() {
      this.$refs.attachmentModal.closeModal()
      setTimeout(() => {
        this.selectedFile = null
        this.selectedFileIndex = -1
        this.selectedFileNote = ''
      }, 100)
    },
    
    saveNote() {
      if (this.selectedFile) {
        this.$emit('update-note', {
          fileId: this.selectedFile.id,
          note: this.selectedFileNote
        })
        this.closeModal()
      }
    }
  }
}
</script>

<style scoped>
.attachment-wrapper {
  margin-top: 10px;
}

.attachment-upload {
  display: inline-flex;
  margin-bottom: 10px;
}

.attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.attachment-card {
  position: relative;
  width: 200px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  cursor: pointer;
}

.attachment-image-wrapper {
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
}

.attachment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-file-icon {
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 4px;
}

.attachment-name {
  font-size: 12px;
  color: #333;
  margin-top: 8px;
  text-align: center;
  word-break: break-all;
  max-width: 100%;
  line-height: 1.3;
  cursor: default;
}

.attachment-note-display {
  width: 100%;
  margin-top: 6px;
  padding: 4px 8px;
  font-size: 11px;
  color: #555;
  background: #f1f5f9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  word-break: break-all;
}

.attachment-note-display i {
  color: #94a3b8;
  font-size: 10px;
  flex-shrink: 0;
}

.attachment-remove {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 50%;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
}

.attachment-remove:hover {
  transform: scale(1.1);
}

/* Modal content styles */
.modal-file-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.modal-image-wrapper {
  width: 200px;
  height: 200px;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
}

.modal-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.modal-file-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px;
}

.modal-file-name {
  font-size: 14px;
  color: #555;
}

.modal-note-wrapper {
  margin-top: 10px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.form-label-thin {
  font-weight: 500;
  font-size: 13px;
  margin-bottom: 5px;
  color: #333;
}

.form-control-thin {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.form-control-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}
</style>

